import React from "react";
import { Link } from "react-router-dom";
import { SakuraFlower, JapaneseSectionHeader, CherryBranchImg } from "./SakuraDecorations";

export default function ServiceBoxes() {
  const services = [
    {
      title: "Akupunktur",
      slug: "/akupunktur",
      icon: "fa-solid fa-spa",
      desc: "Nænsom nålebehandling ved stress, smerter, migræne, søvnproblemer og kæbespændinger.",
      badge: "Klassisk & Moderne",
      japaneseName: "Individuel Nålebehandling",
      cherryIndex: 1,
    },
    {
      title: "Japansk akupunktur",
      slug: "/japansk-akupunktur",
      icon: "fa-solid fa-feather-pointed",
      desc: "Keiraku Chiryo meridianterapi med 0,12 mm ultra-tynde nåle, puls- og mavediagnose.",
      badge: "Særligt Blid",
      japaneseName: "Keiraku Chiryo",
      highlight: true,
      cherryIndex: 2,
    },
    {
      title: "Øre akupunktur",
      slug: "/oere-akupunktur",
      icon: "fa-solid fa-ear-listen",
      desc: "Øret som mikrosystem med små akupunkturnåle, perma nåle og ørekugler for fortsat støtte.",
      badge: "Mikrosystem",
      japaneseName: "Små Nåle & Ørekugler",
      cherryIndex: 3,
    },
    {
      title: "Vægtregulering",
      slug: "/vaegtregulering",
      icon: "fa-solid fa-scale-balanced",
      desc: "Kombineret akupunktur og certificeret asiatisk kostvejledning i et 3 måneders forløb.",
      badge: "3 Mdr. Forløb",
      japaneseName: "Harmoni & Kostvejledning",
      cherryIndex: 5,
    },
    {
      title: "Rygestop behandling",
      slug: "/rygestop-behandling",
      icon: "fa-solid fa-ban-smoking",
      desc: "Dæmper nikotintrang og abstinenser med kropsnåle og sterile ørenåle.",
      badge: "Målrettet Støtte",
      japaneseName: "Mindre Nikotintrang",
      cherryIndex: 6,
    },
  ];

  return (
    <section className="relative z-20 -mt-10 sm:-mt-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Outer Glass Container */}
      <div className="glass-card rounded-3xl shadow-[0_20px_50px_rgba(89,52,39,0.08)] border border-[#ffe0cf] p-6 sm:p-10 lg:p-12 relative overflow-hidden">
        {/* Subtle background texture */}
        <div className="absolute inset-0 bg-shoji-pattern opacity-40 pointer-events-none" />

        {/* Decorative Cherry Branch Illustration in Background */}
        <div className="absolute -top-12 -right-12 w-64 opacity-20 pointer-events-none">
          <CherryBranchImg index={3} className="w-full h-auto" />
        </div>
        <div className="absolute -bottom-16 -left-16 w-56 opacity-15 pointer-events-none">
          <CherryBranchImg index={5} flip={true} className="w-full h-auto" />
        </div>

        <div className="relative z-10">
          <JapaneseSectionHeader
            badge="Behandlinger hos Kita Akupunktur"
            title="Vælg den rette behandling til dine gener"
            subtitle="Individuelt tilpasset akupunktur og traditionel japansk meridianterapi udført med nænsom hånd og ultra-tynde nåle."
            centered={true}
            className="mb-10 sm:mb-12"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7">
            {services.map((s) => (
              <Link
                key={s.slug}
                to={s.slug}
                className={`group relative flex flex-col justify-between p-6 sm:p-7 rounded-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#d74814] overflow-hidden ${
                  s.highlight
                    ? "glass-sakura border-[#ffe0cf] shadow-[0_12px_32px_rgba(215,72,20,0.1)] hover:border-[#d74814]/40"
                    : "bg-white/85 backdrop-blur-xl border border-[#ffe0cf]/80 hover:bg-white hover:border-[#d74814]/30 shadow-[0_4px_20px_rgba(89,52,39,0.04)] hover:shadow-[0_12px_30px_rgba(89,52,39,0.08)]"
                } hover:-translate-y-1`}
              >
                {/* Subtle corner cherry branch graphic */}
                <div className="absolute -right-4 -bottom-4 w-32 opacity-10 group-hover:opacity-25 transition-opacity pointer-events-none">
                  <CherryBranchImg index={s.cherryIndex} className="w-full h-auto" />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-12 h-12 rounded-xl bg-[#ffe0cf]/60 text-[#d74814] flex items-center justify-center group-hover:bg-[#d74814] group-hover:text-white transition-colors duration-200 shadow-2xs border border-[#ffe0cf]">
                      <i className={`${s.icon} text-lg`} aria-hidden="true" />
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-[11px] font-semibold text-[#593427] bg-[#e1ef9f] px-2.5 py-1 rounded-full border border-[#e1ef9f]">
                        {s.badge}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 mb-1.5">
                    <h3 className="text-lg sm:text-xl font-serif font-semibold text-[#593427] group-hover:text-[#d74814] transition-colors">
                      {s.title}
                    </h3>
                    <SakuraFlower className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-[#d74814]" />
                  </div>
                  
                  <span className="text-[11px] uppercase tracking-widest text-[#d74814] block mb-2 font-medium">
                    {s.japaneseName}
                  </span>

                  <p className="text-sm text-[#7a5b4f] leading-relaxed mb-6">
                    {s.desc}
                  </p>
                </div>

                <div className="pt-4 border-t border-[#ffe0cf]/60 flex items-center justify-between text-xs font-semibold text-[#d74814]">
                  <span className="group-hover:translate-x-0.5 transition-transform flex items-center">
                    Læs om behandlingen
                    <i className="fa-solid fa-arrow-right ml-1.5 text-xs text-[#d74814] group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                  </span>
                  <span className="w-6 h-6 rounded-full bg-[#ffe0cf]/40 group-hover:bg-[#ffe0cf] flex items-center justify-center text-[#d74814] transition-colors">
                    <SakuraFlower className="w-3 h-3 text-[#d74814]" />
                  </span>
                </div>
              </Link>
            ))}

            {/* 6th Card: Contact / Booking Card */}
            <div className="flex flex-col justify-between p-6 sm:p-7 rounded-2xl bg-gradient-to-br from-[#593427] to-[#3D2118] text-white shadow-[0_12px_32px_rgba(89,52,39,0.2)] border border-[#593427] relative overflow-hidden">
              <div className="absolute top-0 right-0 w-36 opacity-20 pointer-events-none">
                <CherryBranchImg index={2} flip={true} className="w-full h-auto" />
              </div>

              <div>
                <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-white/15 text-xs text-[#ffe0cf] mb-4 backdrop-blur-xs">
                  <SakuraFlower className="w-3 h-3 text-[#ffe0cf]" />
                  <span>Personlig vejledning</span>
                </div>
                
                <h3 className="text-xl font-serif font-normal text-white mb-2">
                  Er du i tvivl om, hvilken behandling du har brug for?
                </h3>
                
                <p className="text-sm text-[#ffe0cf]/85 leading-relaxed mb-6">
                  Ring uforpligtende eller skriv til Mayumi. Sammen finder vi det rette forløb tilpasset din krop og situation.
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <a
                  href="tel:29387186"
                  className="w-full inline-flex items-center justify-center px-4 py-3 rounded-xl bg-[#d74814] text-white font-medium text-sm hover:bg-[#b83a0d] transition-colors shadow-xs"
                >
                  <i className="fa-solid fa-phone mr-2 text-white" aria-hidden="true" />
                  <span>Ring (+45) 29 38 71 86</span>
                </a>
                
                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-[#ffe0cf] hover:text-white font-medium text-xs border border-white/20 transition-colors"
                >
                  <span>Send en besked online</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
